import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import './styles/App.css';
import './styles/fonts.css';
import SubscriptionPill from './components/SubscriptionPill';
import ErrorBoundary from './components/ErrorBoundary';
import { motion, AnimatePresence } from 'framer-motion';
import GalleryPage from './GalleryPage';
import Information from './components/Information';
import Index from './components/Index';
import { generateImagePaths } from './utils/imageUtils';

// --- Updated Data Structure (Using 'bw') ---
const gigData = [
  { 
    id: 1, 
    bandName: 'Santi & Tuğçe', 
    venue: 'Gretchen', 
    city: 'Berlin', 
    year: '2025', 
    imagePath: '/gallerylist/1.jpg', 
    audioSrc: '/audio/1.mp3', 
    galleryBasePath: '/gallerypage/1', 
    images: { 
      originals: generateImagePaths(1, 'originals'),
      bw: generateImagePaths(1, 'bw')
    }
  },
  { 
    id: 2, 
    bandName: 'new standard jam session', 
    venue: 'Wiener Blut', 
    city: 'Berlin', 
    year: '2025', 
    imagePath: '/gallerylist/2.jpg', 
    audioSrc: '/audio/2.mp3', 
    galleryBasePath: '/gallerypage/2', 
    images: { 
      originals: generateImagePaths(2, 'originals'),
      bw: generateImagePaths(2, 'bw')
    }
  },
  { 
    id: 3, 
    bandName: 'cassette head sessions', 
    venue: '90mil', 
    city: 'Berlin', 
    year: '2025', 
    imagePath: '/gallerylist/3.jpg', 
    audioSrc: '/audio/3.mp3', 
    galleryBasePath: '/gallerypage/3', 
    images: { 
      originals: generateImagePaths(3, 'originals'),
      bw: generateImagePaths(3, 'bw')
    }
  },
  { 
    id: 4, 
    bandName: 'funghi beeps', 
    venue: 'PEPPI GUGGENHEIM', 
    city: 'Berlin', 
    year: '2025', 
    imagePath: '/gallerylist/4.jpg', 
    audioSrc: '/audio/4.mp3', 
    galleryBasePath: '/gallerypage/4', 
    images: { 
      originals: generateImagePaths(4, 'originals'),
      bw: generateImagePaths(4, 'bw')
    }
  },
  { 
    id: 5, 
    bandName: 'Rogér Fakhr and Charif Megarbane', 
    venue: 'gretchen', 
    city: 'Berlin', 
    year: '2025', 
    imagePath: '/gallerylist/5.jpg', 
    audioSrc: '/audio/5.mp3', 
    galleryBasePath: '/gallerypage/5', 
    images: { 
      originals: generateImagePaths(5, 'originals'),
      bw: generateImagePaths(5, 'bw')
    }
  },
  { 
    id: 6, 
    bandName: 'Marshall Vincent and Kaspar Föhres', 
    venue: 'CCA Berlin', 
    city: 'Berlin', 
    year: '2025', 
    imagePath: '/gallerylist/6.jpg', 
    audioSrc: '/audio/6.mp3', 
    galleryBasePath: '/gallerypage/6', 
    images: { 
      originals: generateImagePaths(6, 'originals'),
      bw: generateImagePaths(6, 'bw')
    }
  }
];

// Determine the ID of the last gig for the default view
const defaultGigId = gigData.length > 0 ? gigData[gigData.length - 1].id : null;

// --- Components ---

// Updated Sidebar Component
const LeftNavbar = () => (
  <nav className="left-navbar">
    <Link to="/information" className="nav-section">INFORMATION</Link>
    <Link to="/" className="nav-logo">LUNA ŞAHIN</Link>
    <Link to="/index" className="nav-section">INDEX</Link>
  </nav>
);

// Update GigCard variants for a more "stacking" feel
const slideVariants = {
  initial: { x: 100, opacity: 0 },
  animate: { x: 0, opacity: 1, transition: { duration: 0.4, ease: 'easeInOut' } },
  exit: { x: -100, opacity: 0, transition: { duration: 0.4, ease: 'easeInOut' } },
};

const GigCard = ({ gig, onClick }) => {
  return (
    <motion.div
      key={gig.id}
      className="gig-card active"
      variants={slideVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: 'easeInOut' }}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <div className="gig-text">
        <h2 className="band-name">{gig.bandName}</h2>
        <p className="venue-city">
          {gig.venue && gig.city ? `${gig.venue}, ${gig.city}` : 'Location TBA'}
        </p>
      </div>
    </motion.div>
  );
};

// --- Updated Home Page Component ---
function Home() {
  const [activeGigId, setActiveGigId] = useState(defaultGigId);
  const [previousGigId, setPreviousGigId] = useState(null);
  const [direction, setDirection] = useState(0);
  const navigate = useNavigate();

  const handleSetActive = (id, shouldNavigate = false) => {
    setPreviousGigId(activeGigId);
    const prevIndex = gigData.findIndex(gig => gig.id === activeGigId);
    const nextIndex = gigData.findIndex(gig => gig.id === id);
    setDirection(nextIndex > prevIndex || (prevIndex === gigData.length - 1 && nextIndex === 0) ? 1 : -1);
    setActiveGigId(id);
    if (shouldNavigate) {
      navigate(`/gallery/${id}`);
    }
  };

  const activeGig = gigData.find(gig => gig.id === activeGigId) || (gigData.length > 0 ? gigData[gigData.length - 1] : null);
  const activeIndex = gigData.findIndex(gig => gig.id === activeGigId);
  const imageHeight = 28 + 10; // 28vh + 10px gap

  // Calculate sequential window of three images
  const prevIdx = (activeIndex - 1 + gigData.length) % gigData.length;
  const nextIdx = (activeIndex + 1) % gigData.length;
  const visibleGigs = [gigData[prevIdx], gigData[activeIndex], gigData[nextIdx]];

  // --- Rotated gigs for OtherGigsList ---
  const rotatedGigs = (() => {
    if (gigData.length <= 1) return [];
    const startIdx = (activeIndex + 1) % gigData.length;
    const arr = gigData.slice(startIdx).concat(gigData.slice(0, startIdx));
    return arr.filter(g => g.id !== activeGigId);
  })();

  // Framer Motion variants for rolling animation
  const filmstripVariants = {
    enter: (dir) => ({ y: dir === 1 ? -2 * imageHeight : 0 }),
    center: { y: -imageHeight },
    exit: (dir) => ({ y: dir === 1 ? 0 : -2 * imageHeight })
  };

  return (
    <div className="home-page">
      <div className="home-gallery-container full-vh">
        <LeftNavbar />
        <main className="main-content home-main-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
          <div className="active-gig-wrapper" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2rem' }}>
            <div style={{ marginLeft: '2vw' }}>
              <GigCard key={activeGig.id} gig={activeGig} onClick={() => handleSetActive(activeGig.id, true)} />
            </div>
            <div style={{ overflow: 'hidden', height: 'calc(3 * 28vh + 20px)' }}>
              <AnimatePresence initial={false} custom={direction}>
                <motion.div
                  key={activeGigId}
                  className="gallery-preview-stack"
                  custom={direction}
                  initial={{ y: direction === 1 ? '28vh' : '-28vh', opacity: 0 }}
                  animate={{ y: 0, opacity: 1, transition: { type: 'spring', stiffness: 400, damping: 40 } }}
                  exit={{ y: direction === 1 ? '-28vh' : '28vh', opacity: 0, transition: { type: 'spring', stiffness: 400, damping: 40 } }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}
                >
                  {visibleGigs.map((gig, idx) => (
                    <img
                      key={gig.id}
                      src={process.env.PUBLIC_URL + gig.imagePath}
                      alt={gig.bandName}
                      className={
                        idx === 1
                          ? 'gallery-preview-thumb gallery-preview-thumb-active'
                          : 'gallery-preview-thumb'
                      }
                      style={{ height: '28vh', width: '600px', maxWidth: '60vw', objectFit: 'cover', aspectRatio: '16 / 9', borderRadius: '2px', margin: 0, display: 'block', boxShadow: 'none', cursor: 'pointer' }}
                      onClick={() => handleSetActive(gig.id)}
                    />
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          <div className="other-gigs-container" style={{ marginLeft: '2vw' }}>
            <OtherGigsList
              gigs={rotatedGigs}
              activeId={activeGigId}
              previousId={previousGigId}
              onSelect={handleSetActive}
            />
          </div>
        </main>
      </div>
    </div>
  );
}

// New component to list other gig names
const OtherGigsList = ({ gigs, activeId, previousId, onSelect }) => {
  return (
    <div className="other-gigs-list">
      <AnimatePresence mode="popLayout">
        {gigs.map((gig) => (
          <motion.div
            key={gig.id}
            layout
            className={`other-gig-item ${gig.isPreviousActive ? 'is-previous-active' : ''}`}
            onClick={() => onSelect(gig.id, false)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.4, y: 0, transition: { type: "spring", stiffness: 300, damping: 30 } }}
            exit={{ opacity: 0, y: -20, transition: { type: "spring", stiffness: 300, damping: 30 } }}
            whileHover={{ opacity: 0.8, transition: { duration: 0.2 } }}
          >
            <span className="other-gig-name" onClick={e => {
              e.stopPropagation();
              if (activeId === gig.id) {
                onSelect(gig.id, true);
              } else {
                onSelect(gig.id, false);
              }
            }}>{gig.bandName}</span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

// --- App Component (Routing) ---
function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/gallery/:gigId" element={<GalleryPage />} />
          <Route path="/information" element={<Information />} />
          <Route path="/index" element={<Index />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;

export { gigData };